import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { getNotifications } from '../services/notificationService';
import {
  NotificationRecord,
  NotificationUI,
  TGetNotificationsParams,
  TGetNotificationsResponse,
} from '../types/notifications';

/**
 * Parse notification context to extract meaningful title and description
 */
const parseNotificationContext = (
  notification: NotificationRecord
): { title: string; description: string; type: NotificationUI['type'] } => {
  let context: Record<string, unknown> = {};

  try {
    if (notification.context) {
      context = JSON.parse(notification.context);
    }
  } catch {
    // Context is not JSON, use as plain text
  }

  // Determine type based on notification type or context
  let type: NotificationUI['type'] = 'system';
  const contextType = (context.type as string)?.toLowerCase() || '';
  const notificationType = notification.type?.toLowerCase() || '';

  if (
    contextType.includes('document') ||
    contextType.includes('verification')
  ) {
    type = 'document';
  } else if (
    contextType.includes('alert') ||
    contextType.includes('security')
  ) {
    type = 'alert';
  } else if (
    contextType.includes('transaction') ||
    contextType.includes('payment')
  ) {
    type = 'transaction';
  } else if (contextType.includes('user') || contextType.includes('account')) {
    type = 'user';
  } else if (
    contextType.includes('currency') ||
    contextType.includes('exchange')
  ) {
    type = 'currency';
  } else if (notificationType === 'email') {
    type = 'system';
  }

  // Extract title and description from context
  const title =
    (context.title as string) ||
    (context.subject as string) ||
    `${notification.type} Notification`;
  const description =
    (context.message as string) ||
    (context.body as string) ||
    (context.description as string) ||
    (typeof notification.context === 'string' && notification.context) ||
    `Notification for ${notification.recipient}`;

  return { title, description, type };
};

/**
 * Transform API notification to UI-friendly format
 */
const transformNotification = (
  notification: NotificationRecord,
  readIds: Set<string>
): NotificationUI => {
  const { title, description, type } = parseNotificationContext(notification);

  return {
    id: notification.id,
    title,
    description,
    type,
    time: new Date(notification.created).toLocaleString(),
    timestamp: notification.created,
    isRead: readIds.has(notification.id),
    raw: notification,
  };
};

type UseNotificationsOptions = {
  params?: TGetNotificationsParams;
  queryOptions?: Partial<
    UseQueryOptions<TGetNotificationsResponse, Error, TGetNotificationsResponse>
  >;
};

const useNotifications = (options: UseNotificationsOptions = {}) => {
  const { params, queryOptions } = options;

  // Track read notification IDs in local state
  const [readNotificationIds, setReadNotificationIds] = useState<Set<string>>(
    () => {
      // Try to restore from localStorage
      try {
        const stored = localStorage.getItem('readNotificationIds');
        if (stored) {
          return new Set(JSON.parse(stored));
        }
      } catch {
        // Ignore localStorage errors
      }
      return new Set<string>();
    }
  );

  const {
    data,
    error,
    isFetching,
    isPending,
    isError,
    status,
    refetch,
  } = useQuery({
    queryKey: ['notifications', params],
    queryFn: () => getNotifications(params || {}),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
    retry: false, // Don't retry if endpoint doesn't exist (404)
    ...queryOptions,
  });

  // Check if API is not available (404)
  const isApiUnavailable = useMemo(() => {
    if (!error) return false;
    const axiosError = error as { response?: { status?: number } };
    return axiosError?.response?.status === 404;
  }, [error]);

  // Transform notifications to UI format
  const notifications = useMemo(() => {
    if (!data?.notifications) return [];

    return data.notifications
      .map((n) => transformNotification(n, readNotificationIds))
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [data?.notifications, readNotificationIds]);

  // Calculate unread count
  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.isRead).length;
  }, [notifications]);

  // Mark a notification as read
  const markAsRead = useCallback((id: string) => {
    setReadNotificationIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(id);
      // Persist to localStorage
      try {
        localStorage.setItem('readNotificationIds', JSON.stringify([...newSet]));
      } catch {
        // Ignore localStorage errors
      }
      return newSet;
    });
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setReadNotificationIds((prev) => {
      const newSet = new Set(prev);
      notifications.forEach((n) => newSet.add(n.id));
      // Persist to localStorage
      try {
        localStorage.setItem('readNotificationIds', JSON.stringify([...newSet]));
      } catch {
        // Ignore localStorage errors
      }
      return newSet;
    });
  }, [notifications]);

  // Clear read status (useful for testing or resetting)
  const clearReadStatus = useCallback(() => {
    setReadNotificationIds(new Set());
    try {
      localStorage.removeItem('readNotificationIds');
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  return {
    notifications,
    rawNotifications: data?.notifications || [],
    unreadCount,
    nextCursor: data?.next_cursor,
    error,
    isFetching,
    isPending,
    isError,
    isApiUnavailable,
    status,
    refetch,
    markAsRead,
    markAllAsRead,
    clearReadStatus,
  };
};

export default useNotifications;
